"use client";

import { strings } from "@perceptron/constants";
import { Button } from "@perceptron/ui/components/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@perceptron/ui/components/dialog";
import { Drawer } from "vaul";

import { MODES, type Mode } from "@demo/constants/mode";
import { useIsMobile } from "@demo/hooks/useIsMobile";
import { useMode } from "@demo/hooks/useMode";

import ModeCarousel from "./ModeCarousel";

type ModeSelectorProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * Responsive mode selector. Renders a centered Dialog on desktop and a vaul
 * bottom-sheet Drawer on mobile, each wrapping a {@link ModeCarousel} that opens
 * on the current mode. Switching a mode updates `?mode=` and closes the surface.
 */
const ModeSelector = ({ open, onOpenChange }: ModeSelectorProps) => {
  const [mode, setMode] = useMode();

  const isMobile = useIsMobile();

  const initialIndex = Math.max(0, MODES.indexOf(mode));

  const handleSwitch = (next: Mode) => {
    setMode(next);
    onOpenChange(false);
  };

  if (isMobile) {
    return (
      <Drawer.Root open={open} onOpenChange={onOpenChange}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-[2px]" />
          <Drawer.Content className="fixed inset-x-0 bottom-0 z-50 flex flex-col rounded-t-3xl border-t border-background-alt bg-background outline-none">
            <div aria-hidden className="bg-foreground/20 mx-auto mb-2 h-1 w-10 rounded-full" />
            <Drawer.Title className="sr-only">{strings.modeSwitcher.dialogTitle}</Drawer.Title>
            <Drawer.Description className="sr-only">{strings.modeSwitcher.dialogDescription}</Drawer.Description>
            <ModeCarousel currentMode={mode} initialIndex={initialIndex} variant="mobile" onSwitch={handleSwitch} />
            <Drawer.Close asChild>
              <div className="my-4 px-4">
                <Button className="h-12 w-full rounded-full bg-background-alt-1 text-foreground" size="lg">
                  {strings.modeSwitcher.cancel}
                </Button>
              </div>
            </Drawer.Close>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-w-4xl flex-col items-center gap-8 border-0 bg-background px-0 py-20 sm:rounded-2xl"
        hideCloseButton
      >
        <DialogTitle className="sr-only">{strings.modeSwitcher.dialogTitle}</DialogTitle>
        <DialogDescription className="sr-only">{strings.modeSwitcher.dialogDescription}</DialogDescription>
        <ModeCarousel currentMode={mode} initialIndex={initialIndex} variant="desktop" onSwitch={handleSwitch} />
      </DialogContent>
    </Dialog>
  );
};

export default ModeSelector;
